import { ChangeEvent, useEffect, useState } from "react";

import {
  ArrowButton,
  Typography,
  Button,
  ButtonLink,
  SelectEvent,
  Modal,
} from "@king-ict/eupisi-ui/components";
import {
  useInput,
  useValidation,
  ValidationObject,
  ValidationSchema,
} from "@king-ict/eupisi-ui/hooks";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";

import styles from "./AdministrationCreateUpdate.module.scss";

export interface AdministrationCreateUpdateProps<T> {
  children: (
    state: T,
    errors: ValidationObject<T> | undefined,
    onFieldChange: (
      event: ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
      >
    ) => void
  ) => React.ReactNode;
  type: "read" | "create" | "update";
  data: T;
  backUrl: string;
  backText: string;
  title: string;
  validationSchema: ValidationSchema;
  onSave?: (isCreation: boolean, state: T) => Promise<boolean>;
  onStateChanged?: (state: T, setState: (state: T) => void) => void;
}

export const AdministrationCreateUpdate = <T extends object>({
  children,
  type,
  data,
  backUrl,
  backText,
  title,
  validationSchema,
  onSave,
  onStateChanged,
}: AdministrationCreateUpdateProps<T>) => {
  const [state, setState] = useState({} as T);
  const [isCancelModalOpened, setIsCancelModalOpened] = useState(false);
  const [isMessageModalOpened, setIsMessageModalOpened] = useState(false);

  const history = useHistory();
  const { t } = useTranslation();

  const { handleChange } = useInput();
  const [errors, validate] = useValidation({
    data: state,
    validationSchema: validationSchema,
  });

  useEffect(() => {
    setState({ ...data });
  }, [data]);

  useEffect(() => {
    if (onStateChanged) {
      onStateChanged(state, setState);
    }
  }, [onStateChanged, state]);

  const handleFieldChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | SelectEvent
    >
  ) => {
    handleChange(event, setState);
  };

  const handleToggleCancelModal = () => {
    setIsCancelModalOpened((prev) => !prev);
  };

  const handleConfirmCancelModal = () => {
    history.push(backUrl);
  };

  const handleToggleMessageModal = () => {
    setIsMessageModalOpened((prev) => !prev);
  };

  const handleConfirmMessageModal = () => {
    history.push(backUrl);
  };

  const handleSave = async () => {
    if (validate() && onSave && (await onSave(type === "create", state))) {
      handleToggleMessageModal();
    }
  };

  return (
    <div className={styles.container}>
      <Row>
        <Col xs={12}>
          <div className={styles.backContainer}>
            <ArrowButton
              backwards
              ariaLabel={t("accessibility.backButton")}
              onClick={() => history.push(backUrl)}
            />
            <ButtonLink onClick={() => history.push(backUrl)}>
              {backText}
            </ButtonLink>
          </div>
        </Col>
        <Col xs={12}>
          <Typography
            variant="XLargeBold"
            component={"h1"}
            className={styles.title}
          >
            {title}
          </Typography>
        </Col>
      </Row>
      {children(state, errors, handleFieldChange)}
      {(type === "create" || type === "update") && (
        <Row>
          <Col xs={12} className={styles.buttonsContainer}>
            <Button
              color="primary"
              outline
              onClick={() => handleToggleCancelModal()}
            >
              {t("common.quit")}
            </Button>
            <Button color="primary" onClick={handleSave}>
              {t("common.save")}
            </Button>
          </Col>
        </Row>
      )}

      <Modal
        type={"confirm"}
        title={t("common.cancelModalTitle")}
        cancelText={t("common.no")}
        confirmText={t("common.yes")}
        open={isCancelModalOpened}
        onClose={handleToggleCancelModal}
        onConfirm={handleConfirmCancelModal}
      >
        {t("common.cancelModalMessage")}
      </Modal>

      <Modal
        type={"message"}
        open={isMessageModalOpened}
        confirmText={t("common.close")}
        onConfirm={handleConfirmMessageModal}
        onClose={handleToggleMessageModal}
      >
        {t("common.saveSuccess")}
      </Modal>
    </div>
  );
};
